import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useEffect, useMemo} from 'react';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_SUBPAGE_TYPE,
} from '../../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../../hooks/useAnalytics';
import AurButton from '../../../../common/aur-button/AurButton';

export interface IQueryFieldHeaderDrawerProps {
  title: string;
  applyTitle?: string;
  isBackEnabled?: boolean;
  isApplyDisabled?: boolean;
  componentName?: string;
  onBackClicked?: React.MouseEventHandler<HTMLButtonElement>;
  onCancelClicked?: React.MouseEventHandler<HTMLButtonElement>;
  onApplyClicked?: React.MouseEventHandler<HTMLButtonElement>;
}

const QueryFieldHeaderDrawer: React.FC<IQueryFieldHeaderDrawerProps> = ({
  title,
  applyTitle,
  isBackEnabled = false,
  isApplyDisabled = true,
  onBackClicked,
  onCancelClicked,
  onApplyClicked,
  componentName = '',
}) => {
  const conditionLabel = useMemo(() => t('indexSearchPage.condition'), []);
  const {trackGAEvent} = useAnalytics();

  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name:
          `${title} ` + t('gaTracking.queryBuilder.componentSettings'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  }, []);

  const trackComponent = (buttonLabel: string) => {
    let action = '';
    const subPage = componentName ? componentName : title;
    let details = '';

    if (buttonLabel === t('buttons.apply')) {
      action = t('gaTracking.queryBuilder.applyChanges');
      details = t('gaTracking.queryBuilder.componentAdded', {subPage});
    } else if (buttonLabel === t('buttons.cancel')) {
      action = t('gaTracking.queryBuilder.cancelChanges');
      details = t('gaTracking.queryBuilder.componentCancelled', {subPage});
    } else if (buttonLabel === t('indexSearchPage.backDrawer')) {
      action = t('gaTracking.queryBuilder.navigateBack');
      details = t('gaTracking.queryBuilder.stepBack', {subPage});
    }

    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: buttonLabel,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_SUBPAGE_TYPE.DRAWER,
        action,
        ...(details && {details}),
      },
      subPage: {
        subpage_name:
          `${subPage} ` +
          (isBackEnabled
            ? t('gaTracking.queryBuilder.settingsRule')
            : t('gaTracking.queryBuilder.componentSettings')),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  };
  return (
    <Box className={'query-field-header-container'}>
      <Typography
        className="typo-text subhead3_size color-mist-700"
        aria-hidden={true}
      >
        {conditionLabel}
      </Typography>
      <Box className={'query-field-header-title-container'}>
        <Typography
          className="typo-text headline1_size color-mist-100 aur-query-header-title"
          aria-label={`${conditionLabel} ${title}`}
        >
          {title}
        </Typography>
        <Box className={'query-field-header-button-container'}>
          {isBackEnabled && (
            <AurButton
              dataTestId="query-drawer-back"
              classNames={'drawer-close-button'}
              label={t('indexSearchPage.backDrawer')}
              type={'exit'}
              size={'Medium'}
              onClick={(e) => {
                onBackClicked?.(e);
                trackComponent(t('indexSearchPage.backDrawer'));
              }}
            />
          )}
          <AurButton
            dataTestId="query-drawer-cancel"
            classNames={'drawer-close-button'}
            label={t('indexSearchPage.cancelDrawer')}
            type={'exit'}
            size={'Medium'}
            onClick={(e) => {
              onCancelClicked?.(e);
              trackComponent(t('buttons.cancel'));
            }}
          />
          <AurButton
            dataTestId="query-drawer-apply"
            classNames={'drawer-close-button apply-button'}
            label={applyTitle ?? t('indexSearchPage.applyDrawer')}
            type={'continue'}
            size={'Medium'}
            onClick={(e) => {
              onApplyClicked?.(e);
              trackComponent(t('buttons.apply'));
            }}
            disabled={isApplyDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
};

export {QueryFieldHeaderDrawer};
